import { getTotalDiamondByElementType } from "@/constant/reward";
import { Element, ElementType } from "@/constant/schema";
import { useChatStore } from "@/store/useChatStore";
import Image from "@/components/Image";
import { useMemo, useEffect, useRef, useState } from "react";
import { runDiamondAnimation } from '../Animation/diamondFlyAnimation';
import DiamondFloat, { DiamondFloatRef } from "../Animation/diamondFloatAnimation";
import { colors } from "@/constant/colors";
import { LuBug } from "react-icons/lu";
import ReportPopup from "./ReportPopup";
import { QuestionElementTypes } from "@/constant/element-type-group";
import report, { ReportEvent } from "@/lib/report";

interface CardInfoProps {
  element: Element;
  totalDiamondImageRef: any;
}

const CardInfo = ({ element, totalDiamondImageRef }: CardInfoProps) => {
  const element_map = useChatStore((state) => state.element_map);
  const current_lesson = useChatStore((state) => state.current_lesson);
  const reviewMode = useChatStore((state) => state.reviewMode);
  const questId = useChatStore((state) => state.questId);
  const chapterId = useChatStore((state) => state.chapterId);
  const diamondFloatRef = useRef<DiamondFloatRef>(null);
  const currentDiamondImageRef = useRef<any>(null);
  const [showReportPopup, setShowReportPopup] = useState(false);

  const hasFinished = useMemo(() => {
    return element_map[element.id]?.finished;
  }, [element_map, element.id]);

  const prevFinishedRef = useRef(hasFinished);

  const isQuestion = useMemo(() => {
    return QuestionElementTypes.includes(element.element_type);
  }, [element.element_type]);

  // 监听变化并触发操作
  useEffect(() => {
    if (reviewMode) return;
    // 检查是否从 false 变为 true
    if (prevFinishedRef.current === false && hasFinished === true) {
      
      if (isQuestion) {
        const elementData = element_map[element.id]?.data;
        console.log('Question element data:', elementData);
        
        // 只有回答正确时才显示钻石动画
        if (elementData && elementData.isCorrect) {
          const { startX, startY, endX, endY } = getDiamondPositions();
          runDiamondAnimation({
            start: { x: startX, y: startY },
            end: { x: endX, y: endY },
          });
          diamondFloatRef.current?.play();
        }
      } else {
        const exp = getTotalDiamondByElementType(element?.element_type);
        // 当要增加的金币数量大于0的时候才执行动画，避免+0动画的出现
        if (exp > 0) {
          const { startX, startY, endX, endY } = getDiamondPositions();
          runDiamondAnimation({
            start: { x: startX, y: startY },
            end: { x: endX, y: endY },
          });
          diamondFloatRef.current?.play();
        }
      }
    }

    // 更新前一个值的引用
    prevFinishedRef.current = hasFinished;
  }, [hasFinished, element.element_type, element.id, element_map, reviewMode]);

  const getDiamondPositions = () => {
    return {
      startX: currentDiamondImageRef?.current?.getBoundingClientRect().left,
      startY: currentDiamondImageRef?.current?.getBoundingClientRect().top,
      endX: totalDiamondImageRef?.current?.getBoundingClientRect().left,
      endY: totalDiamondImageRef?.current?.getBoundingClientRect().top,
    };
  };

  // 显示进度：对于问题类型且回答错误，不显示已获得的钻石
  const showEarnedDiamonds = () => {
    if (isQuestion && hasFinished) {
      const userData = element_map[element.id]?.data;
      return userData && userData.isCorrect ? getTotalDiamondByElementType(element?.element_type) : 0;
    }
    return hasFinished ? getTotalDiamondByElementType(element?.element_type) : 0;
  };

  return (
    <>
      <div className="flex flex-row items-center gap-2 relative">
        <span className="text-xl emphasize-text" style={{ color: colors.mainTitleColor }}>{element?.title}</span>
        {
          !reviewMode && (
            <div className={`flex text-xs bg-[#F2ECE1] rounded-full px-2 py-0 w-fit items-center gap-2 ${getTotalDiamondByElementType(element?.element_type) === 0 ? 'hidden' : ''}`}>
              <div className="w-4">
                <Image src={'/assets/diamond-3.png'} ref={currentDiamondImageRef} alt={'diamond'} width={14} height={14} optimizedWidth={50} />
              </div>
              <span className="text-base emphasize-text">{showEarnedDiamonds()}/{getTotalDiamondByElementType(element?.element_type)}</span>
            </div>  
          )
        }        
        {/* Bug report button */}
        <button
          onClick={() => {
            report(ReportEvent.USER_REPORT_BUTTON, {});
            setShowReportPopup(true);
          }}
          className="p-1 hover:bg-gray-100 rounded-full transition-colors duration-200"
          aria-label="Report a problem"
        >
          <LuBug className="w-5 h-5" style={{ color: colors.mainTitleColor }} />
        </button>
      </div>
      <DiamondFloat ref={diamondFloatRef} exp={getTotalDiamondByElementType(element?.element_type)} color={colors.mainTextColor} 
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"/>
      
      {/* Report Popup */}
      <ReportPopup
        isOpen={showReportPopup}
        onClose={() => setShowReportPopup(false)}
        elementTitle={`${element?.title} Card`}
        lessonTitle={current_lesson?.title || `${element?.title} Card`}
        specificElement={element}
        reportType="element"
        contextQuestId={questId}
        contextChapterId={chapterId}
        contextLessonId={current_lesson?.id}
      />
    </>
  );
};

export default CardInfo;